import ASWCNScannerBridge from "./bridge";
import { SCAN_PARAMS, SCANNER_BASE_URL } from "./constants";
import { isAamvaPayload, parseAamva, parseAmbirParsedData } from "./aamva";
import { getPreferredScanner } from "./scannerPreference";
import { ScannerError } from "./errors";

function createBridge() {
  // Always pass an explicit base URL: with none, the vendored bridge falls back
  // to fetching `/api/config` from OUR origin, which we do not serve.
  return new ASWCNScannerBridge(SCANNER_BASE_URL);
}

// Module-scoped guard against overlapping scans. TWAIN allows one application
// at a time: a double-click at the front desk before the first scan resolves
// would otherwise let a second `openSource` race the first (or a first
// `finally`-close release the source out from under the other's in-flight
// `scan()`). Task 6's hook disables the button while scanning, but that is
// UI-level only and doesn't protect a programmatic caller — hence this guard.
let scanInProgress = false;

/** The bridge throws plain Errors carrying the HTTP status in the message. */
function toScannerError(error) {
  if (error instanceof ScannerError) return error;

  const message = error instanceof Error ? error.message : String(error);

  if (message.includes("503")) {
    return new ScannerError(
      "DESKTOP_DISCONNECTED",
      "Scanner desktop app isn't connected."
    );
  }
  if (message.includes("504") || /timed?\s?out/i.test(message)) {
    return new ScannerError(
      "TIMEOUT",
      "Scan timed out — check the card feed and try again."
    );
  }
  return new ScannerError("SCAN_FAILED", message || "Scan failed.");
}

/**
 * Decide which scanner to drive.
 *
 * Returns `choose` rather than picking blindly when several devices are online:
 * Windows desks routinely expose virtual/PDF TWAIN devices that must not be
 * selected silently.
 */
export async function resolveScanner() {
  const bridge = createBridge();

  const available = await bridge.checkServiceStatus();
  if (!available) {
    throw new ScannerError(
      "SERVICE_UNAVAILABLE",
      "AmbirScan Web Connect isn't running on this PC. Start it, or open " +
        `${SCANNER_BASE_URL}/health to trust the certificate.`
    );
  }

  const raw = await bridge.getSources();
  // bridge.js's own JSDoc documents `@returns {Promise<string[]>}` (and its
  // usage example passes an element straight to `openSource(sources[0])`),
  // contradicting the `{ name, isOnline }` shape the rest of this function
  // assumes. Normalise here so the code is correct under either contract —
  // a bare string is treated as an online source with no other metadata.
  const sources = raw.map((source) =>
    typeof source === "string" ? { name: source } : source
  );
  // `isOnline` is optional in practice; absent means "not reported", not "offline".
  const online = sources.filter((source) => source.isOnline !== false);

  if (online.length === 0) {
    throw new ScannerError("NO_SCANNER", "No scanner detected.");
  }

  const names = online.map((source) => source.name);

  if (online.length === 1) return { status: "ready", name: names[0] };

  const preferred = getPreferredScanner();
  if (preferred && names.includes(preferred))
    return { status: "ready", name: preferred };

  return { status: "choose", choices: names };
}

function toDataUrl(image) {
  if (!image) return null;
  const base64 = image.base64Data || image.data || image.base64;
  if (!base64) return null;
  if (base64.startsWith("data:")) return base64;
  const mime = image.mimeType || "image/png";
  return `data:${mime};base64,${base64}`;
}

function findAamvaBarcode(images) {
  const all = images.flatMap((image) => {
    const rawBcs = image.barcodes ?? image.barcode ?? image.barCodes ?? [];
    return Array.isArray(rawBcs) ? rawBcs : [rawBcs];
  });
  return (
    all.find((barcode) => {
      const text =
        barcode?.text ??
        barcode?.data ??
        barcode?.rawData ??
        (typeof barcode === "string" ? barcode : "");
      return barcode?.isAamva === true || isAamvaPayload(text);
    }) ?? null
  );
}

/**
 * Scan a US driver's license in a single duplex pass.
 *
 * Page order gives the image roles (page 1 front, page 2 back), but the barcode
 * is searched across every page — we do not assume which way the card was fed.
 */
export async function scanDriversLicense(scannerName) {
  if (scanInProgress) {
    throw new ScannerError(
      "SCAN_IN_PROGRESS",
      "A scan is already in progress."
    );
  }
  scanInProgress = true;

  const bridge = createBridge();

  try {
    await bridge.openSource(scannerName);
  } catch (error) {
    // A throw here does not necessarily mean the service never opened the
    // source: sendCommand() also throws on network failure and on JSON
    // parse failure, either of which can happen *after* the service opened
    // the source and the response was merely lost in transit. Attempt
    // closeSource() defensively on this path too, inside its own try/catch
    // so it can never mask the original openSource error — a leaked open
    // source is the most likely way to make working hardware look broken
    // to the front desk.
    try {
      await bridge.closeSource();
    } catch (closeError) {
      console.warn(
        "[ambirScanWebConnect] closeSource failed after openSource error",
        {
          scannerName,
          error: closeError,
        }
      );
    }
    scanInProgress = false;
    throw toScannerError(error);
  }

  try {
    const images = await bridge.scan({ ...SCAN_PARAMS });
    const barcode = findAamvaBarcode(images);

    let license = null;
    if (barcode) {
      license = parseAamva(barcode.text);
      if (!license && barcode.parsedData) {
        license = parseAmbirParsedData(barcode.parsedData);
      }
    }

    return {
      frontImage: toDataUrl(images[0]),
      backImage: toDataUrl(images[1]),
      license,
    };
  } catch (error) {
    throw toScannerError(error);
  } finally {
    // TWAIN allows one application at a time. A leaked open source disables the
    // scanner until the Windows service restarts, which the front desk reads as
    // broken hardware — so this must never be skipped, and must never throw over
    // the top of the real error.
    try {
      await bridge.closeSource();
    } catch (closeError) {
      // The scan's own error (or result) must win, so this is deliberately
      // not rethrown or surfaced to the user — but it is logged, not merely
      // ignored: a leaked open source disables the scanner until the Windows
      // service restarts, and this is the only signal anyone gets of that.
      console.warn("[ambirScanWebConnect] closeSource failed", {
        scannerName,
        error: closeError,
      });
    }
    // Release the guard in the same `finally` that closes the source, so a
    // thrown scan doesn't wedge the module permanently.
    scanInProgress = false;
  }
}
